import { gql, useMutation, useQuery } from '@apollo/client'
import * as React from 'react'
import { BodyText, SmallerText, Spacer } from '../../../../common/src/components/core'
import { addFeedback, FeedbackType } from '../../../../common/src/feedback'
import { FetchSettingsQuery, SetSettingMutation, SetSettingMutationVariables } from '../../../../common/src/types.gen'
import EditBox2 from '../components/EditBox2'

const fetchSettingsQuery = gql`
  query FetchSettings {
    settings {
      id
      key
      value
      description
    }
  }
`

const setSettingMutation = gql`
  mutation SetSetting($key: String!, $value: String!) {
    setSetting(key: $key, value: $value) {
      id
      key
      value
      description
    }
  }
`

const SettingsDetail = () => {
  const { loading, error, data } = useQuery<FetchSettingsQuery>(fetchSettingsQuery)
  const [setSetting, setSettingState] = useMutation<SetSettingMutation, SetSettingMutationVariables>(setSettingMutation)

  const handleSaveForKey = React.useCallback(
    async (key: string, value: string) => {
      await setSetting({ variables: { key, value } })
    },
    [setSetting]
  )
  const createHandleSave = React.useCallback(
    (key: string) => (value: string) => handleSaveForKey(key, value),
    [handleSaveForKey]
  )

  React.useEffect(() => {
    if (setSettingState.error) {
      addFeedback(setSettingState.error.message, FeedbackType.ERROR)
    }
  }, [setSettingState])

  const saveHandlers = React.useMemo(() => {
    if (!data?.settings) {
      return {}
    }
    const handlers: Record<string, (val: string) => void> = {}
    for (const setting of data.settings) {
      handlers[setting.key] = (val: string) => {
        createHandleSave(setting.key)(val).catch(console.error)
      }
    }
    return handlers
  }, [data?.settings, createHandleSave])

  if (loading) {
    return <></>
  }

  if (error) {
    return <>{error.message}</>
  }

  if (!data?.settings) {
    return <>Something went wrong</>
  }

  const { settings } = data

  return (
    <div>
      {settings.map(setting => (
        <div key={setting.id}>
          <Spacer height={48} />
          <BodyText>{setting.key}</BodyText>
          <Spacer height={8} />
          <SmallerText>{setting.description}</SmallerText>
          <Spacer height={8} />
          <EditBox2 defaultValue={setting.value} onSave={saveHandlers[setting.key]} type="number" />
        </div>
      ))}
    </div>
  )
}

export default SettingsDetail
